import QuestionPage from '@app-studio-web/ui/pages/QuestionPage'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useSubmit } from 'react-router-dom'
import useUpdateMode from '../../../hooks/useUpdateMode'
import { getAllAnswers, saveAnswer } from '../../../utils/onboardingStorage'

const QUESTION_KEY = 'conditionalClutter'

export default function ConditionalClutterPage() {
  const submit = useSubmit()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const value = searchParams.get('value')
  const preselectedValue = value ? value : undefined
  const { isUpdate, postToReactNative } = useUpdateMode()

  // Get the clutter level from previous answer
  const allAnswers = getAllAnswers()
  const clutterLevelAnswer = allAnswers.clutterLevel
  const clutterLevel = clutterLevelAnswer?.answer_key || '1'

  // Determine if it's minimal (1-3) or cluttered (4-5)
  const isMinimal = parseInt(clutterLevel) <= 3

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    // Get the answer value from the form
    const formData = new FormData(event.currentTarget)
    const selectedValue = formData.get('answer') as string

    if (!selectedValue) return

    const answer = {
      question_key: QUESTION_KEY,
      question: isMinimal
        ? 'Do you want to add more warmth and coziness?'
        : 'Do you want us to suggest smarter storage solutions?',
      answer_key: selectedValue,
      answer: `${selectedValue}`,
    }

    if (isUpdate) {
      postToReactNative(answer)
    } else {
      saveAnswer(QUESTION_KEY, answer)
      submit(event.currentTarget)
    }
  }

  // Options for minimal spaces (1-3 stars)
  const getMinimalOptions = () => [
    {
      value: 'Yes, please',
      translationKey: 'pages.onboarding.conditionalClutter.minimal.options.yes',
      emoji: '☕',
    },
    {
      value: 'No, I like it minimal',
      translationKey: 'pages.onboarding.conditionalClutter.minimal.options.no',
      emoji: '🤍',
    },
  ]

  // Options for cluttered spaces (4-5 stars)
  const getClutteredOptions = () => [
    {
      value: 'Yes, help me organize',
      translationKey:
        'pages.onboarding.conditionalClutter.cluttered.options.yes',
      emoji: '📦',
    },
    {
      value: 'No, just focus on design',
      translationKey:
        'pages.onboarding.conditionalClutter.cluttered.options.no',
      emoji: '🎨',
    },
  ]

  // Get the appropriate options based on clutter level
  const options = isMinimal ? getMinimalOptions() : getClutteredOptions()

  // Transform options to the format expected by QuestionPage
  const buttons = options.map((option) => ({
    text: `${option.emoji} ${t(option.translationKey)}`,
    value: option.value,
  }))

  return (
    <QuestionPage
      heading={t(
        isMinimal
          ? 'pages.onboarding.conditionalClutter.minimal.heading'
          : 'pages.onboarding.conditionalClutter.cluttered.heading',
      )}
      buttons={buttons}
      onSubmit={handleSubmit}
      headingSize="text-3xl text-center whitespace-pre-line"
      buttonTextSize="text-xl"
      buttonBgColor="bg-white"
      preselectedValue={preselectedValue}
    />
  )
}
